import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ProductForm } from "@/components/admin/billing/product-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Package,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";
import type { Product } from "@db/schema";

interface ProductFormData {
  name: string;
  description?: string;
  defaultPrice: string;
  currency: string;
  defaultRecurrence: "monthly" | "annual";
  skuCode?: string;
  defaultServiceTypeId?: string | null;
  customFieldsConfig?: {
    fields: Array<{
      key: string;
      label: string;
      type: "text" | "select" | "boolean";
      options?: string[];
      required?: boolean;
    }>;
  };
}

export default function ProductsPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);

  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ["/api/billing/products"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!search.trim()) return products;
    const term = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.skuCode?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term),
    );
  }, [products, search]);

  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const res = await apiRequest("POST", "/api/billing/products", data);
      return (await res.json()) as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/products"] });
      setDialogOpen(false);
      toast({ title: "Product created successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create product",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ProductFormData;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/billing/products/${id}`,
        data,
      );
      return (await res.json()) as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/products"] });
      setDialogOpen(false);
      setEditingProduct(null);
      toast({ title: "Product updated successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update product",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/products"] });
      setDeleteTarget(null);
      toast({ title: "Product deleted successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete product",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenCreate() {
    setEditingProduct(null);
    setDialogOpen(true);
  }

  function handleOpenEdit(product: Product) {
    setEditingProduct(product);
    setDialogOpen(true);
  }

  function handleFormSubmit(data: ProductFormData) {
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  function getFormDefaults(product: Product | null) {
    if (!product) return undefined;
    const cfg = (product as any).customFieldsConfig as ProductFormData["customFieldsConfig"] | null;
    return {
      name: product.name,
      description: product.description ?? "",
      defaultPrice: product.defaultPrice,
      currency: product.currency,
      defaultRecurrence: product.defaultRecurrence as "monthly" | "annual",
      skuCode: product.skuCode ?? "",
      defaultServiceTypeId: (product as any).defaultServiceTypeId ?? "",
      customFields: cfg?.fields?.map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        options: f.type === "select" && f.options ? f.options.join(", ") : "",
        required: f.required ?? false,
      })) ?? [],
    };
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products"
        description="Manage your product catalog"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[140px] rounded-lg" />
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products found"
          description={
            search
              ? "No products match your search. Try a different term."
              : "Get started by adding your first product."
          }
          action={
            !search ? (
              <Button onClick={handleOpenCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Product
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="transition-colors hover:border-primary/50">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 min-w-0 flex-1">
                    <h3 className="font-semibold text-base truncate">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg font-bold">
                        {formatCurrency(product.defaultPrice, product.currency)}
                        <span className="text-xs font-normal text-muted-foreground ml-1">ex. VAT</span>
                      </span>
                      <Badge variant="secondary" className="capitalize">
                        {product.defaultRecurrence}
                      </Badge>
                    </div>
                    {product.skuCode && (
                      <p className="text-xs text-muted-foreground">
                        SKU: {product.skuCode}
                      </p>
                    )}
                    {product.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    {(product as any).customFieldsConfig?.fields?.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {(product as any).customFieldsConfig.fields.length} custom field(s)
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleOpenEdit(product)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setDeleteTarget(product)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingProduct(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
            <DialogDescription>
              {editingProduct
                ? "Update the product details below."
                : "Fill in the details to create a new product."}
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            key={editingProduct?.id ?? "new"}
            defaultValues={getFormDefaults(editingProduct)}
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Product"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget.id)
        }
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
